import { error, log } from '../../../app/logger.js';
import { EventedBase } from '../base/evented_base.js';

/**
 * Leaflet base class.
 */
class EventedLeaflet extends EventedBase {
  on(eventName, listener) {
    const listenerWrap = nativeEvent => {
      // Leaflet 1.7.1 fires similuated click event on mobile in addition to
      // a normal click. Ignore simulated events as a workaround.
      if (
        nativeEvent.type == 'click' &&
        (!nativeEvent.originalEvent || !nativeEvent.originalEvent.isTrusted)
      ) {
        return;
      }
      // Ignore if event propagation was stopped.
      if (
        nativeEvent.originalEvent &&
        nativeEvent.originalEvent.propogationPrevented
      ) {
        return;
      }
      log(`[map:leaflet] ${nativeEvent.type} on '${this.name}' ${this.type}`);
      let event = {
        type: nativeEvent.type,
        stop: () => {
          // Leaflet rewraps the originalEvent every time it sends an event,
          // and apparently it also process all events in the same event handler,
          // thus stopImmediatePropagation() has no effect.
          if (nativeEvent.originalEvent) {
            nativeEvent.originalEvent.propogationPrevented = true;
          }
        },
        nativeEvent,
      };
      switch (nativeEvent.type) {
        case 'click':
        case 'dblclick':
          Object.assign(event, {
            lat: nativeEvent.latlng.lat,
            lng: nativeEvent.latlng.lng,
            google_id: '',
          });
          break;
      }
      listener(event);
    };

    let nativeEventName = this.#getNativeEventName(eventName);
    if (this.#native) {
      log(
        `[map:leaflet] register ${nativeEventName} event on '${this.name}' ${this.type}`
      );
      this.#native.on(nativeEventName, listenerWrap);
    }

    this.#listeners.push({
      eventName,
      nativeEventName,
      listener,
      listenerWrap,
    });
    return listener;
  }

  off(eventName, listener) {
    for (let i = 0; i < this.#listeners.length; i++) {
      if (
        this.#listeners[i].eventName == eventName &&
        this.#listeners[i].listener == listener
      ) {
        if (this.#native) {
          let { nativeEventName, listenerWrap } = this.#listeners[i];
          log(
            `[map:leaflet] unregister ${nativeEventName} event on '${this.name}' ${this.type}`
          );
          this.#native.off(nativeEventName, listenerWrap);
        }
        this.#listeners.splice(i, 1);
        break;
      }
    }
  }

  // Protected

  get native() {
    return this.#native;
  }

  initialize() {
    this.#native = this.createNative();
    for (let listener of this.#listeners) {
      let { nativeEventName, listenerWrap } = listener;
      log(
        `[map:leaflet] register ${nativeEventName} event on '${this.name}' ${this.type}`
      );
      this.#native.on(nativeEventName, listenerWrap);
    }
  }

  createNative() {
    error(`[map] createNative must be overriden`);
    return null;
  }

  #getNativeEventName(eventName) {
    switch (eventName) {
      case 'bounds_changed':
        return 'move';
      case 'zoom_changed':
        return 'zoomend';
      default:
        return eventName;
    }
  }

  #native;
  #listeners = [];
}

export { EventedLeaflet };
